import { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Toolbar } from '@mui/material'

import Home from './pages/Home'
import Gironi from './pages/Gironi'
import Squadre from './pages/Squadre'
import Arbitri from './pages/Arbitri'
import Arbitro from './pages/Arbitro'
import Distinta from './pages/Distinta'
import Distinte from './pages/Distinte'
import Rimborsi from './pages/Rimborsi'
import Calendari from './pages/Calendari'
import Algoritmi from './pages/Algoritmi'
import Tabelloni from './pages/Tabelloni'
import Tesserati from './pages/Tesserati'
import Iscrizioni from './pages/Iscrizioni'
import Iscrizione from './pages/Iscrizione'
import Rendiconti from './pages/Rendiconti'
import Arbitraggi from './pages/Arbitraggi'
import Classifiche from './pages/Classifiche'
import Spostamenti from './pages/Spostamenti'
import Designazioni from './pages/Designazioni'
import Affiliazioni from './pages/Affiliazioni'
import Affiliazione from './pages/Affiliazione'
import Impostazioni from './pages/Impostazioni'
import Provvedimenti from './pages/Provvedimenti'
import ResetPassword from './pages/ResetPassword'
import DashboardAdmin from './pages/DashboardAdmin'
import IscrizioniCorsi from './pages/IscrizioniCorsi'
import IscrizioneCorsi from './pages/IscrizioneCorsi'
import DashboardSocieta from './pages/DashboardSocieta'
import DashboardArbitro from './pages/DashboardArbitro'
import DashboardDesignatore from './pages/DashboardDesignatore'
import DashboardOmologatore from './pages/DashboardOmologatore'
import Header from './components/Header'
import Drawer from './components/Drawer'
import SnackBar from './components/SnackBar'
import ScrollToTop from './components/ScrollToTop'
import DialogFeedback from './components/DialogFeedback'
import { useAuth } from './hooks/auth-hook'
import { config } from './settings/configs'

let logoutTimer

const App = () => {
  const { auth, admin, societa, arbitro, designatore, omologatore, tokenExp } =
    useSelector(state => state.home.infoUtente)
  const { features } = config
  const { login, logout } = useAuth()

  // auto-login
  useEffect(() => {
    if (auth) return

    try {
      const localToken = JSON.parse(localStorage.getItem('token'))
      if (localToken) login({ token: localToken })
    } catch {
      localStorage.removeItem('token')
    }
  }, [auth, login])

  // auto-logout
  useEffect(() => {
    if (tokenExp) {
      const remainingTime = tokenExp - new Date().getTime()
      const maxTime = 2_000_000_000 // prevent overflow
      logoutTimer = setTimeout(logout, Math.min(remainingTime, maxTime))
    } else {
      clearTimeout(logoutTimer)
    }
  }, [logout, tokenExp])

  const DynamicHome = auth ? (
    (admin && <DashboardAdmin />) ||
    (arbitro && <DashboardArbitro />) ||
    (designatore && <DashboardDesignatore />) ||
    (omologatore && <DashboardOmologatore />) ||
    (societa && <DashboardSocieta />) || <Home />
  ) : (
    <Home />
  )

  const routesPublic = (
    <>
      <Route path='/reset-password' element={<ResetPassword />} />
      {features?.squadre && <Route path='/squadre' element={<Squadre />} />}
      {features?.gironi && <Route path='/gironi' element={<Gironi />} />}
      {features?.calendari && (
        <Route path='/calendari' element={<Calendari />} />
      )}
      {features?.classifiche && (
        <Route path='/classifiche' element={<Classifiche />} />
      )}
      {features?.tabelloni && <Route path='/finali' element={<Tabelloni />} />}
    </>
  )

  const routesAdmin = (
    <>
      {features?.affiliazioni && (
        <Route path='/affiliazione' element={<Affiliazione />} />
      )}
      {features?.affiliazioni && (
        <Route path='/affiliazioni' element={<Affiliazioni />} />
      )}
      {features?.provvedimenti && (
        <Route path='/provvedimenti' element={<Provvedimenti />} />
      )}
      {features?.algoritmi && (
        <Route path='/algoritmi' element={<Algoritmi />} />
      )}
      <Route path='/impostazioni' element={<Impostazioni />} />
    </>
  )

  const routesAdminSocieta = (
    <>
      {features?.iscrizioni && (
        <Route path='/iscrizione' element={<Iscrizione />} />
      )}
      {features?.iscrizioni && (
        <Route path='/iscrizioni' element={<Iscrizioni />} />
      )}
      {features?.spostamenti && (
        <Route path='/spostamenti' element={<Spostamenti />} />
      )}
      {features?.rendiconti && (
        <Route path='/rendiconti' element={<Rendiconti />} />
      )}
      {features?.tesserati && (
        <Route path='/tesserati' element={<Tesserati />} />
      )}
      {features?.distinte && <Route path='/distinta' element={<Distinta />} />}
      {features?.distinte && <Route path='/distinte' element={<Distinte />} />}
    </>
  )

  const routesAdminArbitro = (
    <>
      {features?.rimborsi && <Route path='/rimborsi' element={<Rimborsi />} />}
    </>
  )

  const routesAdminDesignatore = (
    <>{features?.arbitri && <Route path='/arbitri' element={<Arbitri />} />}</>
  )

  const routesAdminSocietaArbitro = (
    <>
      {features?.corsi && (
        <Route path='/iscrizioni-corsi' element={<IscrizioniCorsi />} />
      )}
      {features?.corsi && (
        <Route path='/iscrizione-corsi' element={<IscrizioneCorsi />} />
      )}
    </>
  )

  const routesAdminSocietaDesignatore = (
    <>
      {features?.arbitraggi && (
        <Route path='/arbitraggi' element={<Arbitraggi />} />
      )}
    </>
  )

  const routesAdminArbitroDesignatore = (
    <>
      {features?.arbitri && <Route path='/arbitro' element={<Arbitro />} />}
      {features?.designazioni && (
        <Route path='/designazioni' element={<Designazioni />} />
      )}
    </>
  )

  const routesAuth = (
    <>
      {admin && routesAdmin}
      {(admin || societa) && routesAdminSocieta}
      {(admin || arbitro) && routesAdminArbitro}
      {(admin || designatore) && routesAdminDesignatore}
      {(admin || societa || arbitro) && routesAdminSocietaArbitro}
      {(admin || societa || designatore) && routesAdminSocietaDesignatore}
      {(admin || arbitro || designatore) && routesAdminArbitroDesignatore}
    </>
  )

  return (
    <>
      <ScrollToTop />
      <DialogFeedback />
      <SnackBar />
      <Header />
      <Toolbar />
      <div style={{ display: 'flex' }}>
        <Drawer />
        <Routes>
          <Route path='/' element={DynamicHome} />
          {routesPublic}
          {auth && routesAuth}
          {auth === undefined && <Route path='*' element={DynamicHome} />}
          <Route path='*' element={<Navigate to='/' />} />
        </Routes>
      </div>
      &nbsp;
    </>
  )
}

export default App
